import * as d3 from "d3";
import MitMapSelectionService from './MitMapSelectionService.js';

class MitMapService {
    constructor(svgId, objectDefinitions, selectChangedCallback, highlightChangedCallback, editObjectCallback) {
        this.svg = d3.select(svgId);
        this.data = null;

        //TYPES of events that can be raised on the callback below
        this.EDIT_OBJECT_TYPE = {
            CREATED:'CREATED',
            MODIFIED:'MODIFIED',
            DELETED:'DELETED'
        }
        this.editObjectCallback = editObjectCallback;

        this.serviceSelection = new MitMapSelectionService(selectChangedCallback, highlightChangedCallback);

        this.objectDefinitions = objectDefinitions;
        this.objectToCreate = null;
        this.editmode = false;
        this.editModeAllowModify = false;

        this.projection = null;
        this.path = null;
        this.zoom = null;

        this.layerMap = null;
        this.layerPrevLevel = null;
        this.layerCurrentLevel = null;
        this.layerObjects = null;

        this.selected = {};//space or object that is currently in focus

        this.colorCodes = {
            'BACKGROUND':'#DDC7A8',
            'GRAY_LIGHT':'#F0F0F0',
            'GRAY_DARK': '#E2E2E2',
            'CORRIDOR':'#E5D5BE',
            'STAIRWAY':'#A5A5A4',
            'ELEVATOR':'#A5A5A4',
            // 'OFFICE SERVICE':'#E5D5BE'
        };
        
    }

    setHighlightMouseOver(highlight){
        this.highlight = highlight;
    }
    setEditMode(editmode){
        this.editmode = editmode;
    }
    setEditModelAllowModify(allow, existingObjectMitIdsList){
        this.editModeAllowModify = allow;
        this.existingObjectMitIdsList = existingObjectMitIdsList;
    }
    deselect(){
        this.serviceSelection.unselect(this.serviceSelection.currentlySelected.space, this.serviceSelection.currentlySelected.d3Element);
    }
    select(mitId){
        let item = d3.select("#mit_" + mitId);
        this.serviceSelection.select(item.data()[0], item);
    }

    changeCreateObject(obj){
        this.objectToCreate = obj;

        if(this.objectToCreate){
            this.svg.style("cursor", "crosshair");
        }
        else {
            this.svg.style("cursor", "default");
        }
    }

    createGeojsonModel(){
        return {
          type: "FeatureCollection",
          features: []
        };
    }
    deleteSelectedObject(){
        this.serviceSelection.currentlySelected.d3Element.remove();
        this.editObjectCallback(this.EDIT_OBJECT_TYPE.DELETED, this.serviceSelection.currentlySelected.space, this.serviceSelection.currentlySelected.d3Element);
    }
    rotateSelectedObject(angle){
        let s = this.serviceSelection.currentlySelected.space;
        if(s && s.properties && s.properties.spaceType === 'object'){
            let d = this.serviceSelection.currentlySelected.d3Element;

            let centerPoint = this.path.centroid(this.serviceSelection.currentlySelected.space);

            d.attr("transform", "rotate(" + angle + ", " + centerPoint[0] + ", " + centerPoint[1] + ")");

            this.editObjectCallback(this.EDIT_OBJECT_TYPE.MODIFIED, this.serviceSelection.currentlySelected.space, this.serviceSelection.currentlySelected.d3Element);
        }
    }

    getObjectScreenSize(space){
        // p1-------p4
        // |         |
        // |         |
        // |         |
        // p2-------p3
        let p1 = this.projection(space.geometry.coordinates[0][0]);
        let p2 = this.projection(space.geometry.coordinates[0][1]);
        let p3 = this.projection(space.geometry.coordinates[0][2]);
        let p4 = this.projection(space.geometry.coordinates[0][3]);

        //pythagorean theorem to find point distances
        //d=√((x_2-x_1)²+(y_2-y_1)²)
        return {
            width:Math.sqrt( Math.pow((p3[0] - p2[0]),2) + Math.pow((p3[1] - p2[1]),2) ),
            height:Math.sqrt( Math.pow((p2[0] - p1[0]),2) + Math.pow((p2[1] - p1[1]),2) )
        };
    }

    changeSizeSelectedObject(amount){
        let s = this.serviceSelection.currentlySelected.space;
        let d = this.serviceSelection.currentlySelected.d3Element;
        let centerPoint = this.path.centroid(s);//get centroid in screen coordinates
        let size = this.getObjectScreenSize(s);

        size.width = size.width * amount;
        size.height = size.height * amount;

        if(s && s.properties && s.properties.spaceType === 'object' & size.width > 3 && size.width < 65 && size.height > 3 && size.height < 65){

            let p1 = this.projection.invert([centerPoint[0]-(size.width/2),centerPoint[1]-(size.height/2)]);
            let p2 = this.projection.invert([centerPoint[0]-(size.width/2),(centerPoint[1]-(size.height/2))+size.height]);
            let p3 = this.projection.invert([centerPoint[0]-(size.width/2)+size.width,(centerPoint[1]-(size.height/2))+size.height]);
            let p4 = this.projection.invert([centerPoint[0]-(size.width/2)+size.width,centerPoint[1]-(size.height/2)]);

            s.geometry.coordinates = [[ p1,p2,p3,p4,p1]];
            s.properties.geometryCoordinates = [[p1,p2,p3,p4,p1]];

            d.datum(s).attr("d", (s) => this.path(s))
                      .style('background-size', size.width + 'px' + ' ' + size.height + 'px');

            this.editObjectCallback(this.EDIT_OBJECT_TYPE.MODIFIED, this.serviceSelection.currentlySelected.space, this.serviceSelection.currentlySelected.d3Element);
        }

    }

    checkObjectNameValid(list, id){
        if(list.hasOwnProperty(id))
            return false;

        return true;
    }
    makeNameSafeForId(name){
        return name.replace(/[^a-zA-Z0-9]/g, "").replace(' ', '').toLowerCase();
    }
    renameObjectInfo(existingMitIdList, previousName, newName, mitId, mitIdShort){
        return {
            mitId: mitId.replace(this.makeNameSafeForId(previousName), this.makeNameSafeForId(newName)),
            mitIdShort:mitIdShort.replace(this.makeNameSafeForId(previousName), this.makeNameSafeForId(newName)),
            name:newName
        };
    }
    getObjectInfo(existingMitIdList, parentMitId, parentMitIdShort, objectUseType, objectName){
        let safeName = this.makeNameSafeForId(objectName);

        let info = {
            mitId:parentMitId + '-' + objectUseType+'-'+safeName,
            mitIdShort:parentMitIdShort + '-' + objectUseType+'-'+safeName,
            name:objectName
        }

        //Generate a new name if there is a collision
        if(this.checkObjectNameValid(existingMitIdList, info.mitId) === false){
            let nextId = 1;

            while(this.checkObjectNameValid(existingMitIdList, info.mitId+nextId) == false){
                nextId++;
            }

            info.mitId = info.mitId+nextId;
            info.mitIdShort = info.mitIdShort+nextId;
            info.name = info.name+nextId;
        }

        return info;
    }

    addObject(objToCreate, mouseX, mouseY, parentSpace){
        if(this.editModeAllowModify){
            //convert the screen coordinates to gis using our projection
            // p1-------p4
            // |         |
            // |         |
            // |         |
            // p2-------p3
            let p1 = this.projection.invert([mouseX-(objToCreate.width/2),mouseY-(objToCreate.height/2)]);
            let p2 = this.projection.invert([mouseX-(objToCreate.width/2),(mouseY-(objToCreate.height/2))+objToCreate.height]);
            let p3 = this.projection.invert([mouseX-(objToCreate.width/2)+objToCreate.width,(mouseY-(objToCreate.height/2))+objToCreate.height]);
            let p4 = this.projection.invert([mouseX-(objToCreate.width/2)+objToCreate.width,mouseY-(objToCreate.height/2)]);

            let objInfo = this.getObjectInfo(this.existingObjectMitIdsList, parentSpace.properties.mitId, parentSpace.properties.mitIdShort, objToCreate.type, objToCreate.name)
            let feature = {
                    type: "Feature",
                    geometry: {
                        type: "Polygon",
                        coordinates: []
                    },
                    properties:{
                        mitId:objInfo.mitId,
                        mitIdShort:objInfo.mitIdShort,
                        mitBuildingId:parentSpace.properties.mitBuildingId,
                        name:objInfo.name,
                        description:'',
                        level:parentSpace.properties.level,
                        sqfoot:null,
                        org:'',
                        useType:objToCreate.type,
                        useCategory:objToCreate.type+'S',
                        rotation:0,
                        spaceType:'object',
                        version: parentSpace.properties.version,
                        parentId: parentSpace.properties.id,
                        geometryCoordinates:[],
                        geometryType:'Polygon'
                    }
            };
            //polygon needs a closing point, that is why p1 is added again.
            feature.geometry.coordinates.push([ p1,p2,p3,p4,p1]);
            feature.properties.geometryCoordinates.push([ p1,p2,p3,p4,p1]);

            let createdOject = this.layerObjects.append("path");

                createdOject.datum(feature)
                            .attr("d", (feature) => this.path(feature))
                            .attr("fill", "url(#"+objToCreate.type.replace(/ /g,"_")+")")
                            .attr("cursor", "grab")
                            .on("click", (event, obj) => {
                                if(this.editmode)
                                    this.serviceSelection.select(obj, d3.select(event.target));
                                else
                                    this.serviceSelection.selectEvent(obj, d3.select(event.target));
                            })
                            .on("mouseover", (event, obj) => {
                                if(!this.editmode)
                                    this.serviceSelection.highlight(obj, d3.select(event.target));
                            })
                            .on("mouseleave", (event, obj) => {
                                if(!this.editmode)
                                    this.serviceSelection.highlight(obj, d3.select(event.target));
                            });

                createdOject.call(d3.drag()
                    .on("start", (event, space) => {
                        this.dragStartEvent(event,space);
                    })
                    .on("drag", (event, space) => {
                        this.dragEvent(event,space);
                    })
                    .on("end", (event, space) => {
                        this.dragEndEvent(event,space);
                    })
                );

                this.serviceSelection.select(feature, createdOject);
                this.editObjectCallback(this.EDIT_OBJECT_TYPE.CREATED, feature, createdOject);
        }

    }

    convertGeoJSONtoMapModel(geoJSON){

        let mapModel = {};

        let level,spaceType;
        let levels = [];

        for(let x = 0; x < geoJSON.features.length; x++){
            level = geoJSON.features[x].properties.level;
            levels.push(level);
            spaceType = geoJSON.features[x].properties.spaceType;

            if(mapModel.hasOwnProperty(spaceType) === false){
                mapModel[spaceType] = {};
            }

            if(mapModel[spaceType].hasOwnProperty(level) === false){
                mapModel[spaceType][level] = {
                    "type": "FeatureCollection",
                    "features": []
                };
            }

            mapModel[spaceType][level].features.push(geoJSON.features[x]);

        }

        //CREATE default object model since initially none will exist
        //Ensure there is an objects layer since the map requires it to be able to add objects.
        for(let x = 0; x < levels.length; x++){
            //Create a default object model if the building has none, usually this would be after initially imported where users have not added anything yet.
            if(mapModel.hasOwnProperty('object') === false){
                mapModel['object'] = {};
            }
            //Create a default object layer for the level
            if(mapModel['object'].hasOwnProperty(levels[x]) === false){
                mapModel['object'][levels[x]] = {
                    "type": "FeatureCollection",
                    "features": []
                };
            }
        }

        return mapModel;

    }

    enableZoom(zoomInElementId, zoomOutElementId, zoomResetElementId){
        this.zoomControls = {
            in:zoomInElementId,
            out:zoomOutElementId,
            reset:zoomResetElementId
        }

        this.zoom = d3.zoom()
        .scaleExtent([.3, 10])
        .on("zoom", function (e) {
            d3.select('svg #group-map').attr('transform',e.transform);
        });

        this.svg.call(this.zoom)
                .on("wheel.zoom", null); //prevent zooming while scrolling

        this.addZoomEvents();
    }

    createMap(spacesModel, objectsModel, doorsModel, windowsModel, structuresModel, floorDict, width, height, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg){
        this.clearMap();

        this.svg.attr("width", '100%')
                .attr("height", '100vh');

        this.addUIObjectsDefinitions(this.objectDefinitions);

        //I think this is ok to scope it to just the spaces and the selected floor
        //doing this for performance, but I have not tested performance, soo maybe this isn't necessary
        this.createProjection(spacesModel[selectedFloor], width, height);

        this.createLayers();

        this.currentFloor = floorDict[selectedFloor];

        if(spacesModel){
            this.renderLayerPrevSpaces(spacesModel[selectedFloor-1]);
            this.renderLayerSpaces(spacesModel[selectedFloor], selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg);
        }
        if(objectsModel){
            if(typeof objectsModel[selectedFloor] === 'undefined' || objectsModel[selectedFloor] === null){
                //if there are no objects for this floor then it will be empty, so lets just init
                objectsModel[selectedFloor] = this.createGeojsonModel();
            }

            this.renderLayerObjects(objectsModel[selectedFloor], selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg);
        }
        if(structuresModel){
            this.renderLayerStructures(structuresModel[selectedFloor][0], selectedFloor);
        }
        if(windowsModel){
            this.renderLayerWindows(windowsModel[selectedFloor], selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg);
        }
        if(doorsModel){
            this.renderLayerDoors(doorsModel[selectedFloor], selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg);
        }

    }

    clearMap(){
        if(this.svg){
            this.svg.selectAll("*").remove();
            this.svg.attr("width", 10)
                    .attr("height", 10);
        }
    }

    addUIObjectsDefinitions(objectList){
        let defs = this.svg.append("defs");

        for(let x = 0; x < objectList.length; x++){
            defs.append('pattern')
                .attr('id', objectList[x].type.replace(/ /g,"_"))
                .attr('width', 1)
                .attr('height', 1)
                .attr('patternContentUnits', 'objectBoundingBox')
                .append("image")
                    .attr("href", objectList[x].iconPath)
                    .attr('preserveAspectRatio', 'none')
                    .attr('width', 1)
                    .attr('height', 1);
        }

    }

/* ****************************************************
INTERNAL FUNCTIONS
******************************************************* */

    getSpaceColor(spaceProperties){
        if(spaceProperties.commonArea)
            return this.colorCodes.CORRIDOR;
        if(spaceProperties.stairsElevator)
            return this.colorCodes.ELEVATOR;
        if(spaceProperties.color)
            return spaceProperties.color;
        return this.colorCodes[spaceProperties.useType];
    }
    getPreFloorSpaceColor(spaceProperties){
        return this.colorCodes[spaceProperties.useType];
    }

    addZoomEvents(){
        d3.select(this.zoomControls.in).on("click", () => {
            this.svg.transition().call(this.zoom.scaleBy, 2);
        });
        d3.select(this.zoomControls.out).on("click", () => {
            this.svg.transition().call(this.zoom.scaleBy, 0.5);
        });
        d3.select(this.zoomControls.reset).on("click", () => {
            this.svg.transition().call(this.zoom.scaleTo, 1);
        });
    }

    dragStartEvent(event, space) {
        if(this.editModeAllowModify){
            space.dragControl = d3.select(event.sourceEvent.target)
              .attr("cursor", "grabbing")
              .attr("stroke", null);
              //.raise();
            //this captures the delta for this drag event only
            //used to compute the new coordinates
            space.dragX = 0;
            space.dragY = 0
        }
    }
    dragEvent(event, space) {
        if(this.editModeAllowModify){
            //Update the html representation of the object and the data using transform of mouse delta
            //This will NOT update the data in space.

            //Current position:
            //This is used to maintain the accumulated xy offset as the UI is working only
            //off the total delta since rendered.
            space.properties.rotation = space.properties.rotation || 0;//seed a rotation of 0 if none exists to make the translation work
            space.dragAccumulatedX = space.dragAccumulatedX || 0;
            space.dragAccumulatedY = space.dragAccumulatedY || 0;
            //Update position with the delta x and y applied by the drag:
            space.dragAccumulatedX += event.dx;
            space.dragAccumulatedY += event.dy;

            //Update the delta since last drag, for calculating new data coordinates since last drag.
            space.dragX += event.dx;
            space.dragY += event.dy;

            let centerPoint = this.path.centroid(space);

            // Apply the translation to the shape:
            //Since we are moving based on a shifting transform, we can't get rid of the offset until the
            //entire object is re-rendered in the drop end
            //This seems to be an efficient way to move the object with the mouse and maintain the rotation.
            space.dragControl.attr("transform", "translate(" + space.dragAccumulatedX + "," + space.dragAccumulatedY + ") rotate(" + space.properties.rotation + ", " + centerPoint[0] + ", " + centerPoint[1] + ")");
        }
    }

    dragEndEvent(event, space) {
        if(this.editModeAllowModify){
            //set the cursor back to grab
            space.dragControl.attr("cursor", "grab");

            //Update the parent information in case the user dropped this into another space
            let parent = this.findParentSpace(event.x, event.y);



            //only move if there is a valid room parent, for now.
            //We can make the entire floor a valid parent but need to get this information available
            //Remove OR add this if condition we want to allow moving to anywhere on map. The parent will just be assigned to the floor we are on.
            //if(parent){
                let parentId = this.currentFloor.id;
                let parentMidId = this.currentFloor.mitId;
                let parentMitIdShort = this.currentFloor.mitIdShort;

                if(parent){
                    parentId = parent.properties.id;
                    parentMidId = parent.properties.mitId;
                    parentMitIdShort = parent.properties.mitIdShort;
                }

                //No need to change or update the ids or names if not moved outside the parent.
                //If we do, the name will be updated since the check to make sure the new moved object
                //is unique in the list of existing obj mitIds and update it :(
                if(space.properties.parentId !== parentId){
                    //remove the old id from the existing list
                    //this is ugly, this code for managing the list is all over the place
                    //need a service to manage this
                    delete this.existingObjectMitIdsList[space.properties.mitId];

                    let objInfo = this.getObjectInfo(this.existingObjectMitIdsList, parentMidId, parentMitIdShort, space.properties.useType, space.properties.name)
                    space.properties.parentId = parentId;
                    space.properties.mitId = objInfo.mitId;
                    space.properties.mitIdShort = objInfo.mitIdShort;
                    space.properties.name = objInfo.name; //update the name in case there is a move collision.

                    //add the new name
                    //this is ugly, this code for managing the list is all over the place
                    //please refactor and add a service that can be used as a central place for htis logic.
                    this.existingObjectMitIdsList[space.properties.mitId] = 1;
                }

                //IMPORTANT, use the dragX and dragY delta since last drag and not the accumulated or
                //else the object will be exponetially shifted wayyyy off the screen :)
                let delta = {
                    x:space.dragX,
                    y:space.dragY
                };

                //This is a bit ugly, it feels like there should be a better way to do this.
                //1. take the gis coordinates in the data space model and convert them to screen coordinates
                //2. Add the delta of the mouse move while dragging to each point.
                //3. convert the points back to GIS coordiantes using the projection
                let screenPoints = []
                let point = [];
                for(let x = 0; x < space.geometry.coordinates[0].length; x++){
                    point = this.projection(space.geometry.coordinates[0][x]);
                    point[0] += delta.x;
                    point[1] += delta.y;
                    screenPoints.push(point);
                }

                let newPoints = []
                for(let x = 0; x < screenPoints.length; x++){
                    newPoints.push(this.projection.invert(screenPoints[x]));
                }

                space.geometry.coordinates = [newPoints];
                space.properties.geometryCoordinates = [newPoints];

            //}



            let centerPoint = this.path.centroid(space);

            space.dragControl.datum(space)
                             .attr("d", (f) => this.path(f))
                             .attr("transform", "rotate(" + space.properties.rotation + ", " + centerPoint[0] + ", " + centerPoint[1] + ")");
                             //.attr("transform", "translate(" + space.dragAccumulatedX + "," + space.dragAccumulatedY + ") rotate(" + space.properties.rotation + ", " + centerPoint[0] + ", " + centerPoint[1] + ")");

            this.serviceSelection.selectApplyUI(space, space.dragControl);
            this.editObjectCallback(this.EDIT_OBJECT_TYPE.MODIFIED, space, space.dragControl);

            //This removes the dragControl and other drag variables
            this.dragRemoveData(space);
        }
    }
    dragRemoveData(space){
        //cleanup, remove the drag properties we added.
        delete space.dragControl;
        delete space.dragX;
        delete space.dragY;
        delete space.dragAccumulatedX;
        delete space.dragAccumulatedY;
    }

    polygonContainsPoint(point, polygon) {
        var x = point[0], y = point[1];

        var inside = false;
        for (var i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            var xi = polygon[i][0], yi = polygon[i][1];
            var xj = polygon[j][0], yj = polygon[j][1];

            var intersect = ((yi > y) != (yj > y))
                && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }

        return inside;
    }

    findParentSpace(mouseX,mouseY){
        let gisPoint = this.projection.invert([mouseX,mouseY]);
        let parent = null;
        this.layerSpaces.selectAll('path').each( (space, index) => {
            if(this.polygonContainsPoint(gisPoint, space.geometry.coordinates[0])){
                parent = space;
            }
        });

        return parent;
    }

    createProjection(geojson, width, height){
        this.projection = d3.geoMercator()
                      .rotate([90, 28.5, 0])
                      .fitSize([width, height], geojson);

        // Create a GeoPath function from the projection
        this.path = d3.geoPath().projection(this.projection);
    }

    createLayers(){

        //ORDER MATTERS!!!
        this.layerMap = this.svg.append("g").attr("id", "group-map");
        this.layerPrevSpaces = this.layerMap.append("g");
        this.layerSpaces = this.layerMap.append("g");
        this.layerStructure = this.layerMap.append("g");
        this.layerDoors = this.layerMap.append("g");
        this.layerWindows = this.layerMap.append("g");
        this.layerObjects = this.layerMap.append("g");
        this.layerText = this.layerMap.append("g");

    }

    renderLayerStructures(structureModel, selectedFloor){
        if(typeof structureModel === 'undefined' || structureModel === null || structureModel.length === 0)
            return;

        this.layerStructure.selectAll("line")
            .data(structureModel)
            .enter()
            .append("line")
            .attr("x1", d=>this.projection(d[0])[0])
            .attr("y1", d=>this.projection(d[0])[1])
            .attr("x2", d=>this.projection(d[1])[0])
            .attr("y2", d=>this.projection(d[1])[1])
            .attr("stroke", 'black')
            .style("stroke-width", ".5px");
    }
    renderLayerDoors(doorsModel, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg){
        if(typeof doorsModel === 'undefined' || doorsModel === null || doorsModel.features.length === 0)
            return;

        this.layerDoors.selectAll("path")
            .data(doorsModel.features)
            .enter()
            .append("path")
            .attr("d", (feature) => this.path(feature))
            .attr("id", (feature) => 'mit_'+feature.properties.mitId)
            .style("stroke-width", (f) => {
                if(f.properties.mitId == selectedSpaceMitId)
                    return "2px";

                return "1px"
            })
            .attr("stroke", (f) => {
                if(f.properties.mitId == selectedSpaceMitId)
                    return "#70E73D";
                if(f.properties.useType == selectedUseTypes)
                    return "#F45B33";

                return "#6D4A33"

            })
            .on("click", (event, obj) => {
                if(this.editmode)
                    this.serviceSelection.select(obj, d3.select(event.target));
                else
                    this.serviceSelection.selectEvent(obj, d3.select(event.target));
            })
            .on("mouseover", (event, obj) => {
                if(this.highlight)
                    this.serviceSelection.highlight(obj, d3.select(event.target));
            })
            .on("mouseleave", (event, obj) => {
                if(this.highlight)
                    this.serviceSelection.unhighlight(obj, d3.select(event.target));
            })
    }

    renderLayerWindows(windowsModel, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg){
        if(typeof windowsModel === 'undefined' || windowsModel === null || windowsModel.length === 0)
            return;
        this.layerWindows.selectAll("line")
            .data(windowsModel)
            .enter()
            .append("line")
            .attr("x1", d=>this.projection(d[0])[0])
            .attr("y1", d=>this.projection(d[0])[1])
            .attr("x2", d=>this.projection(d[1])[0])
            .attr("y2", d=>this.projection(d[1])[1])
            .attr("stroke", (f) => {
                if(selectedUseTypes == 'WINDOW')
                    return "#F45B33";

                return "#758CB3"

            })
            .style("stroke-width", ".5px");

    }

    renderLayerObjects(objectsModel, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg){
        if(typeof objectsModel === 'undefined')
            return;

        this.layerObjects.selectAll("path")
            .data(objectsModel.features)
            .enter()
            .append("path")
            //.attr("d", (feature) => this.path(feature))
            .attr("d", (feature) => {
                //while we are in here converting the path
                //lets make sure we reset any drag information
                this.dragRemoveData(feature);
                return this.path(feature)
            })
            .attr("id", (feature) => 'mit_'+feature.properties.mitId)
            .attr("stroke", (f) => {
                if(f.properties.mitId == selectedSpaceMitId)
                    return "#70E73D";

                if(f.properties.useType == selectedUseTypes)
                    return "#F45B33";

                return null;

            })
            .attr("stroke-width", (f) => {
                if(f.properties.useType == selectedUseTypes || f.properties.mitId == selectedSpaceMitId)
                    return "2px";

                return null

            })
            .attr("fill", (f) => {
                return "url(#"+f.properties.useType.replace(/ /g,"_")+")";//a use type maps to an image if we want to show an image for that type.
            })
            .attr("transform", (f) => {
                let centerPoint = this.path.centroid(f);
                return "rotate(" + f.properties.rotation + ", " + centerPoint[0] + ", " + centerPoint[1] + ")";
            })
            .on("click", (event, obj) => {
                if(this.editmode)
                    this.serviceSelection.select(obj, d3.select(event.target));
                else
                    this.serviceSelection.selectEvent(obj, d3.select(event.target));
            })
            .on("mouseover", (event, obj) => {
                if(this.highlight)
                    this.serviceSelection.highlight(obj, d3.select(event.target));
            })
            .on("mouseleave", (event, obj) => {
                if(this.highlight)
                    this.serviceSelection.unhighlight(obj, d3.select(event.target));
            })
            .call(d3.drag()
                .on("start", (event, space) => {
                    this.dragStartEvent(event,space);
                })
                .on("drag", (event, space) => {
                    this.dragEvent(event,space);
                })
                .on("end", (event, space) => {
                    this.dragEndEvent(event,space);
                }));

    }

    renderLayerPrevSpaces(spacesModel, selectedFloor){
        if(typeof spacesModel === 'undefined')
            return;

        this.layerPrevSpaces.selectAll("path")
            .data(spacesModel.features)
            .enter()
            .append("path")
            // .filter((f) => {
            //     if(typeof f.properties === 'undefined')
            //         return false;
            //
            //     return true;
            // })
            .attr("d", (feature) => this.path(feature))
            .attr("stroke", (f) => {
                return "#9F9F9F";
            })
            .attr("fill", (f) => {
                if(this.getPreFloorSpaceColor(f.properties))
                    return this.getPreFloorSpaceColor(f.properties);
                else
                    //return this.colorCodes.GRAY_LIGHT
                    return "#EBEBCC"//DCE2E8
            })
            .style("stroke-width", "1px")
            .style("opacity", .6);
    }

    renderLayerSpaces(spacesModel, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg){

        this.layerSpaces.selectAll("path")
            .data(spacesModel.features)
            .enter()
            .append("path")
            // .filter((f) => {
            //     if(typeof f.properties === 'undefined')
            //         return false;
            //     return true;
            // })
            .attr("d", (feature) => this.path(feature))
            .attr("id", (feature) => 'mit_'+feature.properties.mitId)
            .attr("fill", (f) => {
                if(f.properties.useType == selectedUseTypes)
                    return "#F69F88";

                if(f.properties.mitId == selectedSpaceMitId)
                    return "#A9F688";
                else if(this.getSpaceColor(f.properties))
                    return this.getSpaceColor(f.properties);
                else{
                    if(selectedOrg == null || f.properties.org == selectedOrg)
                        return 'white';
                    else
                        return "#E8E8E8"
                }

            })
            .attr("opacity", (f) => {
                return 1;
                if(selectedOrg == null || f.properties.org == selectedOrg)
                    return 1;
                else
                    return .4
            })
            .style("stroke-width", "1px")
            .on("mouseover", (event, space) => {
                if(this.highlight)
                    this.serviceSelection.highlight(space, d3.select(event.target));
            })
            .on("click",  (event, space) => {
                if(this.objectToCreate){
                    let m = d3.pointer(event);
                    this.addObject(this.objectToCreate, m[0], m[1], space);
                }
                else{
                    if(this.editmode)
                        this.serviceSelection.select(space, d3.select(event.target));
                    else
                        this.serviceSelection.selectEvent(space, d3.select(event.target));
                }
            })
            .on("mouseleave", (event, space) => {
                // this.unselectSpace(space, event);
                if(this.highlight)
                    this.serviceSelection.unhighlight(space, d3.select(event.target));
            });

            this.layerText.selectAll("text")
                .data(spacesModel.features)
                .enter()
                .append("svg:text")
                .filter((f) => {
                    if(typeof f.properties === 'undefined')
                        return false;

                    if(selectedOrg && f.properties.org != selectedOrg)
                        return false;

                    // if(selectedOrg && f.properties.org == selectedOrg && f.properties.level === selectedFloor)
                    //     return true;

                    if(f.properties.level === selectedFloor &&
                        (
                            f.properties.useType == 'OFFICE' ||
                            f.properties.useType == 'LECTURE HALL'||
                            f.properties.useType == 'OFFICE SERVICE'||
                            f.properties.useType == 'RESEARCH LAB'||
                            f.properties.useType == 'RSCH LAB-OFFICE'||
                            f.properties.useType == 'LABORATORY SERVICE'||
                            f.properties.useType == 'LABORATORY SVC'||
                            f.properties.useType == 'SECY/RECEPTION'||
                            f.properties.useType == 'LAB SUPPORT SHOP'||
                            f.properties.useType == 'CLASSROOM'||
                            f.properties.useType == 'CLASSROOM SVC'||
                            f.properties.useType == 'COMPUTER FACIL'||
                            f.properties.useType == 'COMPUTER SERVICE'||
                            f.properties.useType == 'LOUNGE'||
                            f.properties.useType == 'CONFERENCE ROOM'||
                            f.properties.useType == 'STUDY ROOM'||
                            f.properties.useType == 'MULTI-PURPOSE RM' ||
                            f.properties.showRoomNumber
                            
                        )
                    )
                        return true;

                    return false;
                })
                .text((d) => {
                    return d.properties.mitIdShort;
                })
                .attr("x", (d) => {
                    return this.path.centroid(d)[0];
                })
                .attr("y", (d) => {
                    return  this.path.centroid(d)[1];
                })
                .attr("text-anchor","middle")
                .attr('font-size','4pt')
                .call(function getBB(selection) {
                    selection.each(function(d){d.bbox = this.getBBox();})
                });

            this.layerText.selectAll("rect")
                .data(spacesModel.features)
                .enter()
                .append("svg:rect")
                .filter((f) => {
                    if(typeof f.properties === 'undefined')
                        return false;

                    if(selectedOrg && f.properties.org != selectedOrg)
                        return false;

                    // if(selectedOrg && f.properties.org == selectedOrg && f.properties.level === selectedFloor)
                    //     return true;

                    if(f.properties.level === selectedFloor &&
                        (
                            f.properties.useType == 'OFFICE' ||
                            f.properties.useType == 'LECTURE HALL'||
                            f.properties.useType == 'OFFICE SERVICE'||
                            f.properties.useType == 'RESEARCH LAB'||
                            f.properties.useType == 'RSCH LAB-OFFICE'||
                            f.properties.useType == 'LABORATORY SERVICE'||
                            f.properties.useType == 'LABORATORY SVC'||
                            f.properties.useType == 'SECY/RECEPTION'||
                            f.properties.useType == 'LAB SUPPORT SHOP'||
                            f.properties.useType == 'CLASSROOM'||
                            f.properties.useType == 'CLASSROOM SVC'||
                            f.properties.useType == 'COMPUTER FACIL'||
                            f.properties.useType == 'COMPUTER SERVICE'||
                            f.properties.useType == 'LOUNGE'||
                            f.properties.useType == 'CONFERENCE ROOM' ||
                            f.properties.useType == 'STUDY ROOM'||
                            f.properties.useType == 'MULTI-PURPOSE RM'||
                            f.properties.showRoomNumber
                            
                            
                        )
                    )
                        return true;

                    return false;
                })
                .attr("x", function(d){return d.bbox.x})
                .attr("y", function(d){return d.bbox.y})
                .attr("width", function(d){return d.bbox.width})
                .attr("height", function(d){return d.bbox.height})
                .attr("fill","white");

                //raise the text back on top of the rectangles we just drew
                this.layerText.selectAll("text").raise()
                .call(function getBB(selection) {
                    selection.each(function(d){delete d.bbox;})
                });
    }


}

export default MitMapService;
